/**
 * Дефолты: разметка тактики поверх карты.
 *
 * Нарисованное хранится не картинкой, а списком фигур: так его можно
 * перерисовать в любом размере, отменить последнее действие и переписать
 * пресет, не теряя остального. Картинкой оно становится только при выгрузке.
 *
 * Координаты — доли ширины и высоты карты (0..1), а не пиксели. Карта на
 * экране растягивается под окно, а пресет открывают и на ноутбуке, и на
 * телефоне: в пикселях стрелка уезжала бы мимо дома.
 */

/** Сторона, за которую нарисован дефолт. Та же пара, что в статистике каптов. */
export type TacticSide = 'defence' | 'attack';

export const TACTIC_SIDE_LABEL: Record<TacticSide, string> = {
  defence: 'В защите',
  attack: 'В атаке',
};

export function isTacticSide(value: unknown): value is TacticSide {
  return value === 'defence' || value === 'attack';
}

/** Точка в долях карты: [x, y], каждая от 0 до 1. */
export type TacticPoint = readonly [number, number];

/**
 * Цвета разметки.
 *
 * Список закрыт, потому что цвет уходит в базу и потом в разметку страницы:
 * произвольная строка оттуда — это чужой код на странице.
 *
 * Первый — цвет по умолчанию. Чёрный: карты каптов светлые и пёстрые, и
 * чёрная линия читается на них лучше любой цветной. Красный взят открытый и
 * яркий, а не приглушённый: приглушённый терялся в кирпичных крышах.
 */
export const TACTIC_COLORS: readonly string[] = [
  '#000000',
  '#ff0033',
  '#ff9500',
  '#ffd60a',
  '#32d74b',
  '#0a84ff',
  '#bf5af2',
  '#ffffff',
];

/**
 * Цвета, которых больше нет в палитре, но которые могли попасть в дефолты.
 *
 * Разбор их пропускает: иначе смена палитры молча выбросила бы из чужого
 * пресета все фигуры такого цвета.
 */
const TACTIC_LEGACY_COLORS: readonly string[] = ['#ff4d4d', '#1c1c1e'];

/**
 * Толщина линии в долях ширины карты — чтобы не худеть при увеличении.
 *
 * Величины подобраны по доске, а не по картинке: карта 2400 px показывается
 * в панели примерно семисотпиксельной, и прежние 0,002 давали на экране
 * полтора пикселя — тонкий маркер было не разглядеть.
 */
export const TACTIC_WIDTHS: readonly number[] = [0.003, 0.006, 0.011];

/**
 * Пределы, за которыми разметка перестаёт быть разметкой.
 *
 * Нужны не для красоты: пресет приходит с клиента и целиком ложится в базу
 * одной строкой. Без границ туда уедет мегабайт точек с зажатой кистью.
 */
export const TACTIC_LIMITS = {
  shapes: 400,
  pointsPerStroke: 1200,
  labelLength: 3,
  nameLength: 60,
  /** Мест в машине. Больше в игре и не садится. */
  crew: 4,
  crewNameLength: 24,
} as const;

/** Линия от руки: маркер сплошной или пунктирный. */
export interface TacticStroke {
  kind: 'stroke';
  color: string;
  width: number;
  /** Пунктиром рисуют то, что под вопросом: запасной путь, вариант. */
  dashed: boolean;
  /** Точки подряд: [x1, y1, x2, y2, ...] — вдвое компактнее списка пар. */
  points: number[];
}

/**
 * Что означает стрелка.
 *
 * На капте это два разных приказа, и путать их нельзя: «идём туда» и
 * «стреляем туда» ведут состав в разные места. Поэтому они и рисуются
 * по-разному — шагами и прицелом, — а не только цветом.
 */
export type TacticArrowVariant = 'move' | 'fire';

export const TACTIC_ARROW_LABEL: Record<TacticArrowVariant, string> = {
  move: 'Заход',
  fire: 'Огонь',
};

/** Стрелка: куда идёт состав или куда он стреляет. */
export interface TacticArrow {
  kind: 'arrow';
  variant: TacticArrowVariant;
  color: string;
  width: number;
  from: TacticPoint;
  to: TacticPoint;
}

/** Метка: кружок с номером или буквой — «здесь первый», «здесь снайпер». */
export interface TacticMarker {
  kind: 'marker';
  color: string;
  at: TacticPoint;
  label: string;
}

/**
 * Машина с экипажем.
 *
 * Ники сохраняются текстом, а не идентификаторами Discord: дефолт — это
 * снимок замысла на конкретный капт, и он должен читаться через месяц, даже
 * если человек сменил ник или ушёл из семьи. Связь с набором нужна в момент
 * рисования, дальше она только мешала бы.
 */
export interface TacticCar {
  kind: 'car';
  color: string;
  at: TacticPoint;
  /**
   * Какой картинкой рисовать: номер по кругу, а не имя файла.
   *
   * Машины на доске должны отличаться друг от друга с одного взгляда, поэтому
   * каждая новая берёт следующую картинку. Номер, а не имя, потому что набор
   * картинок живёт во фронте и может смениться — а дефолт от этого ломаться
   * не должен.
   */
  image: number;
  /** Кто едет: до четырёх ников. Пустая машина тоже машина — сажают потом. */
  crew: string[];
  /**
   * Куда сдвинули плашку с экипажем. `null` — стоит там, куда её положила
   * раскладка.
   *
   * Хранится в фигуре, а не считается: расставив плашки сам, человек ждёт,
   * что они останутся на месте и после сохранения дефолта.
   */
  labelAt: TacticPoint | null;
}

export type TacticShape = TacticStroke | TacticArrow | TacticMarker | TacticCar;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Доля карты: за края не выпускаем, но и не отбрасываем фигуру целиком. */
function clampUnit(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return Math.min(1, Math.max(0, Math.round(value * 10000) / 10000));
}

function readPoint(value: unknown): TacticPoint | null {
  if (!Array.isArray(value) || value.length !== 2) {
    return null;
  }

  const x = clampUnit(value[0]);
  const y = clampUnit(value[1]);
  return x === null || y === null ? null : [x, y];
}

/** Цвет только из палитры: произвольную строку в разметку пускать нельзя. */
function readColor(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  return TACTIC_COLORS.includes(value) || TACTIC_LEGACY_COLORS.includes(value) ? value : null;
}

/** Ближайшая разрешённая величина: клиент мог прислать своё, но не любое. */
function readFrom(list: readonly number[], value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return list.reduce((best, item) => (Math.abs(item - value) < Math.abs(best - value) ? item : best), list[0] ?? 0);
}

function readShape(value: unknown): TacticShape | null {
  if (!isRecord(value)) {
    return null;
  }

  const color = readColor(value['color']);
  if (color === null) {
    return null;
  }

  if (value['kind'] === 'stroke') {
    const width = readFrom(TACTIC_WIDTHS, value['width']);
    const raw = value['points'];
    if (width === null || !Array.isArray(raw) || raw.length < 4 || raw.length % 2 !== 0) {
      return null;
    }

    const points: number[] = [];
    for (const item of raw.slice(0, TACTIC_LIMITS.pointsPerStroke * 2)) {
      const unit = clampUnit(item);
      if (unit === null) {
        return null;
      }

      points.push(unit);
    }

    return { kind: 'stroke', color, width, dashed: value['dashed'] === true, points };
  }

  if (value['kind'] === 'arrow') {
    const width = readFrom(TACTIC_WIDTHS, value['width']);
    const from = readPoint(value['from']);
    const to = readPoint(value['to']);
    if (width === null || from === null || to === null) {
      return null;
    }

    // Незнакомый вид стрелки — это заход: он безобиднее приказа стрелять.
    const variant: TacticArrowVariant = value['variant'] === 'fire' ? 'fire' : 'move';
    return { kind: 'arrow', variant, color, width, from, to };
  }

  if (value['kind'] === 'car') {
    const at = readPoint(value['at']);
    const raw = value['crew'];
    if (at === null) {
      return null;
    }

    const crew = (Array.isArray(raw) ? raw : [])
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim().slice(0, TACTIC_LIMITS.crewNameLength))
      .filter((item) => item !== '')
      .slice(0, TACTIC_LIMITS.crew);

    const image = typeof value['image'] === 'number' && Number.isFinite(value['image'])
      ? Math.max(0, Math.trunc(value['image']))
      : 0;

    const labelAt = readPoint(value['labelAt']);

    return { kind: 'car', color, at, image, crew, labelAt };
  }

  if (value['kind'] === 'marker') {
    const at = readPoint(value['at']);
    const label = typeof value['label'] === 'string' ? value['label'].trim().slice(0, TACTIC_LIMITS.labelLength) : '';
    return at === null || label === '' ? null : { kind: 'marker', color, at, label };
  }

  return null;
}

/**
 * Разбирает разметку, пришедшую с клиента.
 *
 * Битая фигура выбрасывается, а не роняет запрос: пресет рисуют долго, и
 * терять его целиком из-за одной кривой стрелки обиднее, чем потерять её.
 */
export function parseTacticShapes(value: unknown): TacticShape[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const shapes: TacticShape[] = [];
  for (const item of value) {
    if (shapes.length >= TACTIC_LIMITS.shapes) {
      break;
    }

    const shape = readShape(item);
    if (shape !== null) {
      shapes.push(shape);
    }
  }

  return shapes;
}

/**
 * Карта, поверх которой рисуют.
 *
 * Ключ — имя файла без расширения, а не номер в базе: карты лежат картинками
 * в `server/assets/tactic-maps`, и список собирается по содержимому папки.
 * Номер пришлось бы держать в базе и сверять с файлами при каждом запуске.
 */
export interface TacticMap {
  id: string;
  name: string;
  /** Размеры картинки: по ним доска знает свои пропорции до её загрузки. */
  width: number;
  height: number;
}

/** Сохранённый дефолт. */
export interface TacticPreset {
  id: number;
  mapId: string;
  name: string;
  side: TacticSide;
  shapes: TacticShape[];
  updatedAt: string;
  updatedBy: string | null;
}

/**
 * Ключ карты в адресе запроса.
 *
 * Имя файла приходит с клиента, а по нему открывается файл на диске: без
 * этой проверки «../../etc/passwd» был бы обычным ключом карты.
 */
export function isTacticMapId(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 64) {
    return false;
  }

  // Разрешены только буквы, цифры, пробел, дефис и подчёркивание: из них
  // нельзя составить путь. Точка и косые черты не проходят, а с ними и «..».
  return /^[\p{L}\p{N} _-]+$/u.test(value);
}
